import React, { useState } from "react";
import { toFroApi } from "../../api/toFroRegisterApi";
import { 
  GlobalButton, 
  GlobalTextField, 
  GlobalTextAreaField 
} from "../../../../global/forms/GlobalFormFields";
import { toMysqlDateTime } from "../../utils/mysqlDatetime";
import {
  datetimeLocalNow,
  mysqlOrIsoToDatetimeLocalMin,
  validateReturnInTimeAgainstOut,
} from "../../utils/datetimeLocalBounds";
import "../../styles/markReturnedModal.css";

const MarkReturnedModal = ({ isOpen, onClose, movement, onSuccess }) => {
  const [formData, setFormData] = useState({
    in_time: "",
    remarks: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when modal opens with new movement
  React.useEffect(() => {
    if (isOpen && movement) {
      setFormData({
        in_time: "",
        remarks: "",
      });
      setErrors({});
    }
  }, [isOpen, movement]);

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setErrors(prev => ({ ...prev, [field]: "" }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.in_time) {
      newErrors.in_time = "In time is required";
    } else {
      const max = datetimeLocalNow();
      if (formData.in_time > max) {
        newErrors.in_time = "In time cannot be in the future";
      } else {
        const outRef = movement?.__rawOutTime ?? movement?.out_time;
        const order = validateReturnInTimeAgainstOut(formData.in_time, outRef);
        if (!order.ok) {
          newErrors.in_time = order.message;
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const returnData = {
        in_time: toMysqlDateTime(formData.in_time),
        remarks: formData.remarks,
      };
      
      await toFroApi.markReturned(movement.id, returnData);
      
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error marking returned:", error);

      const apiMsg =
        typeof error.response?.data?.message === "string" ? error.response.data.message.trim() : "";

      if (error.response?.status === 400 && apiMsg) {
        setErrors({ in_time: apiMsg });
        return;
      }
      if (error.response?.status === 409) {
        alert("This movement has already been returned.");
        onClose();
      } else if (error.response?.status === 404) {
        alert("Movement not found.");
        onClose();
      } else {
        alert(apiMsg || "Failed to mark movement as returned. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !movement) return null;

  return (
    <div className="mrm-modal-overlay">
      <div className="mrm-modal">
        <div className="mrm-modal__header">
          <h2 className="mrm-modal__title">
            Mark as Returned
          </h2>
        </div>
        
        <div className="mrm-modal__body">
          {/* Movement Info */}
          <div className="mrm-movement-info">
            <h4>
              Movement Details
            </h4>
            <div className="mrm-movement-info-grid">
              <div className="mrm-movement-info-item">
                <div className="mrm-movement-info-label">Staff</div>
                <div className="mrm-movement-info-value">
                  {movement.name || movement.staff_user_id}
                </div>
              </div>
              <div className="mrm-movement-info-item">
                <div className="mrm-movement-info-label">Out Time</div>
                <div className="mrm-movement-info-value">
                  {new Date(movement.out_time).toLocaleString()}
                </div>
              </div>
              <div className="mrm-movement-info-item">
                <div className="mrm-movement-info-label">Place</div>
                <div className="mrm-movement-info-value">
                  {movement.place_visited}
                </div>
              </div>
              <div className="mrm-movement-info-item">
                <div className="mrm-movement-info-label">Purpose</div>
                <div className="mrm-movement-info-value">
                  {movement.purpose_name || movement.purpose_id}
                </div>
              </div>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="mrm-form">
            <GlobalTextField
              label="In Time"
              type="datetime-local"
              value={formData.in_time}
              onChange={(value) => handleChange("in_time", value)}
              required
              error={errors.in_time}
              min={mysqlOrIsoToDatetimeLocalMin(movement?.__rawOutTime ?? movement?.out_time)}
              max={datetimeLocalNow()}
            />
            <div className="mrm-form__hint">
              System will automatically calculate duration
            </div>

            <GlobalTextAreaField
              label="Remarks"
              value={formData.remarks}
              onChange={(value) => handleChange("remarks", value)}
              placeholder="Enter any additional remarks about return"
              rows={3}
            />
          </form>
        </div>

        <div className="mrm-modal__footer">
          <GlobalButton
            onClick={onClose}
            variant="ghost"
            size="md"
            disabled={loading}
            className="mrm-button--secondary"
          >
            Cancel
          </GlobalButton>
          <GlobalButton
            type="submit"
            onClick={handleSubmit}
            variant="primary"
            size="md"
            disabled={loading}
            className="mrm-button--success"
          >
            {loading && (
              <div className="mrm-spinner"></div>
            )}
            {loading ? "Saving..." : "Mark Returned"}
          </GlobalButton>
        </div>
      </div>
    </div>
  );
};

export default MarkReturnedModal;
